import React, { useMemo } from "react"
import { useRouter } from "next/router"
import Image from "next/image"
import Link from "next/link"

import { useAuthenticatedUser, useMachine, useProfile, useRestoredUniverse } from "@lib"

import { AffAttendeeProfile, AffRenewalOrder } from "@firmament-packages/aff"
import { Subscription, RenewalOrder } from "@firmament-packages/paid_memberships"
import { PmtPayment } from "@firmament-packages/payments"
import "../../firmament-packages/aff/extensions/AffAttendeeProfileExtensions"
import "../../firmament-packages/aff/extensions/SubscriptionExtensions"
import "../../firmament-packages/aff/extensions/AffRenewalOrderExtensions"

import AFFSidebarLayout from "@components/domain/AFFSidebarLayout"
import { AffAttendeeProfileMenuStateMachine } from "@machines/AffAttendeeProfileMenuStateMachine"
import { OMReference } from "@apptivity-lab/firmament-node-sdk"
import LoadingIndicator from "@components/LoadingIndicator"
import ErrorHUD from "@components/ErrorHUD"
import FreeTrialTag from "@firmament-packages/aff/components/FreeTrialTag"

const MenuItem: React.FunctionComponent<{
    title: string,
    showDisclosureIndicator?: boolean,
    onClick?: () => void
}> = ({ title, showDisclosureIndicator, onClick }) => {
    return (
        <div className="flex items-center h-[60px] px-[20px] text-body text-black-900 cursor-pointer" onClick={() => onClick && onClick()}>
            <div className="flex-1">{title}</div>
            {showDisclosureIndicator && <Image width="24" height="24" src="/images/ic_chevron_right_accent.svg" alt="Disclosure Indicator" />}
        </div>
    )
}

const CurrentSubscriptionDetail: React.FunctionComponent<{
    subscription?: Subscription,
    latestPayment?: PmtPayment
}> = ({ subscription, latestPayment }) => {
    const promptUpgrade = useMemo(() => {
        if ((!subscription || (subscription?.product?.actualObject?.sequence !== 0))) {
            return true
        }
        return (latestPayment && latestPayment.status !== "CHARGED")
    }, [subscription, latestPayment])

    if (subscription && subscription.subscriptionProcessor === "MANUAL" && !subscription.endAt) {
        return (
            <div className="mt-[4px] p-[16px] bg-appBackground rounded-[8px]">
                <h4 className="text-headline text-black-900">
                    <div className="flex items-center">
                        {subscription.product?.actualObject?.title}
                        {subscription.isTrial && <FreeTrialTag />}
                    </div>
                </h4>

                <p className={["block mt-[8px] text-footnote text-black-700"].join(" ")}>
                    {subscription?.product?.actualObject?.description}
                </p>
            </div>
        )
    }

    return (
        <Link href="/profile/membership">
            <a>
                <div className="mt-[4px] p-[16px] flex justify-between items-center bg-appBackground rounded-[8px]">
                    <div>
                        <h4 className="text-headline text-black-900">
                            {!subscription && <>AFF Free</>}
                            {subscription &&
                                <div className="flex items-center">
                                    {subscription.product?.actualObject?.title}
                                    {subscription.isTrial && <FreeTrialTag />}
                                </div>
                            }
                        </h4>
                        <p className={["mt-[8px] text-footnote", latestPayment?.failureReason ? "text-alert-900" : "text-black-700"].join(" ")}>
                            {!subscription && <>Access free classes only</>}
                            {subscription && !subscription.isRenewing &&
                                <>Ending on {subscription.endAtShortDisplay()}</>
                            }
                            {subscription && subscription.isRenewing &&
                                <>Next renewal on {subscription.endAtShortDisplay()}</>
                            }
                        </p>

                        {subscription && subscription?.requiresSetup() && (subscription.product?.actualObject?.sequence === 0) && subscription.isTrial === true &&
                            <p className="mt-[8px] text-callout text-primary-900 underline">
                                Stay unlimited
                            </p>
                        }
                        {promptUpgrade &&
                            <p className="mt-[8px] text-callout text-primary-900 underline">
                                Upgrade for full access
                            </p>
                        }
                    </div>

                    <Image width="24" height="24" src="/images/ic_chevron_right_accent.svg" alt="Disclosure Indicator" />
                </div>
            </a>
        </Link>
    )
}

const Content: React.FunctionComponent<{
    attendeeProfile: AffAttendeeProfile,
    onLogout?: () => void
}> = ({ attendeeProfile, onLogout }) => {
    const router = useRouter()

    const currentSubscription = attendeeProfile.currentSubscription()

    const orders = useMemo(() => {
        return currentSubscription?.renewalOrders?.reduce((result: AffRenewalOrder[], current: OMReference<RenewalOrder>) => {
            const renewalOrder = new OMReference(AffRenewalOrder, current.id).actualObject
            if (renewalOrder) {
                result.push(renewalOrder)
            }
            return result
        }, [])
    }, [currentSubscription])

    const latestPayment = useMemo(() => {
        const sortedOrders = (orders || []).sort((a, b) => b.modifiedAt.getTime() - a.modifiedAt.getTime())
        if (sortedOrders.length === 0) {
            return null
        }
        return sortedOrders[0].latestPayment()
    }, [orders])

    return (
        <div>
            {latestPayment?.failureReason && <div className="fixed w-full px-[20px] py-[16px] bg-red-900 z-20">
                <Link href="/profile/orders">
                    <a>
                        <div className="flex justify-between items-center">
                            <p className="text-footnote text-white-900">
                                {"We can't process your payment. Please click here to update your payment information."}
                            </p>
                            <Image width="24" height="24" src="/images/ic_chevron_right_white.svg" alt="Payment Processing Issue" />
                        </div>
                    </a>
                </Link>
            </div>}

            <div className={["bg-primary-100 min-h-[72vh] pb-[72px]", latestPayment?.failureReason ? "pt-[100px]" : "sm:pt-[32px]"].join(" ")}>
                <div id="profileMenuHeader" className="w-100 sm:max-w-[480px] mx-auto px-[40px] py-[32px] bg-white-900 sm:rounded-[8px] shadow">
                    <div className="relative w-[80px] h-[80px] mx-auto overflow-hidden rounded-full">
                        <Image layout="fill" objectFit="cover"
                            src={attendeeProfile?.photo?.actualObject?.url || "/images/ic_avatar.svg"}
                            alt="Profile Photo" />
                    </div>

                    <div className="mt-[16px] text-black-900 text-center">
                        <h2 className="text-title3">{attendeeProfile?.fullName || "Guest"}</h2>
                        <div className="mt-[4px] text-footnote text-tertiary-900">Classes Attended: {attendeeProfile.attendedMeetingCount}</div>
                    </div>

                    <div className="flex mt-[24px] space-x-[16px] text-center">
                        <div className="flex-1 px-[8px] py-[16px] bg-appBackground rounded-[8px]">
                            <h4 className="text-footnote text-black-500">Member Since</h4>
                            <div className="mt-[4px] text-headline text-black-900">{attendeeProfile?.createdAt?.toLocaleDateString("en-US", { day: "numeric", month: "short", year: "numeric" }) || "N/A"}</div>
                        </div>

                        <div className="flex-1 p-[16px] bg-appBackground rounded-[8px]">
                            <h4 className="text-footnote text-black-500">Class Passes</h4>
                            <div className="mt-[4px] text-headline text-black-900">
                                {attendeeProfile.availableSessionsBalanceDisplay()}
                            </div>
                        </div>
                    </div>

                    <div className={["mt-[16px] text-footnote", latestPayment?.failureReason ? "text-alert-900" : "text-tertiary-900"].join(" ")}>Current Membership</div>
                    {
                        <CurrentSubscriptionDetail
                            subscription={currentSubscription || undefined}
                            latestPayment={latestPayment || undefined} />
                    }
                </div>

                <div id="accountMenu" className="w-100 sm:max-w-[480px] mt-[32px] mx-auto">
                    <h1 className="px-[20px] text-title2 text-black-700">Account</h1>

                    <div className="mt-[12px] bg-white-900 sm:rounded-[8px] shadow">
                        <MenuItem title="Edit Profile" showDisclosureIndicator={true} onClick={() => router.push("/profile/edit")} />

                        {attendeeProfile.participationType === "INDIVIDUAL" &&
                        <>
                            <hr className="border-black-100" />
                            <MenuItem title="Payment Information" showDisclosureIndicator={true} onClick={() => router.push("/profile/orders")} />
                        </>
                        }

                        <hr className="border-black-100" />
                        <MenuItem title="My Past Classes" showDisclosureIndicator={true} onClick={() => router.push("/profile/past-classes")} />

                        <hr className="border-black-100" />
                        <MenuItem title="Change Email" showDisclosureIndicator={true} onClick={() => router.push("/profile/change-email")} />
                    </div>

                    <div className="mt-[40px] h-[60px] bg-white-900 sm:rounded-[8px] shadow cursor-pointer">
                        <button type="button" className="flex w-full items-center px-[20px] h-[60px] text-body text-alert-900"
                            onClick={() => onLogout && onLogout()}>
                            Log Out
                        </button>
                    </div>
                </div>
            </div>
        </div>
    )
}

function ProfileMenuPage() {
    const router = useRouter()

    useAuthenticatedUser("/auth/login")

    const profile = useProfile(AffAttendeeProfile)

    const [machine, machineState] = useMachine(AffAttendeeProfileMenuStateMachine)
    useRestoredUniverse(useMemo(() => () => {
        if (profile) {
            machine.load(new OMReference(AffAttendeeProfile, profile))
        }
    }, [machine, profile]))

    switch (machineState.name) {
        case "start":
            return null
        case "loading":
            return (
                <>
                    <LoadingIndicator />
                    {machineState.profileId.actualObject && <Content attendeeProfile={machineState.profileId.actualObject} onLogout={() => machine.logout()} />}
                </>
            )
        case "presenting":
            return <Content attendeeProfile={machineState.profile} onLogout={() => machine.logout()} />
        case "showingError":
            return (
                <>
                    <ErrorHUD error={machineState.error} sticky={false} />
                    {machineState.profile && <Content attendeeProfile={machineState.profile} onLogout={() => machine.logout()} />}
                </>
            )
        case "loggingOut":
            return (
                <>
                    <LoadingIndicator message="Logging out..." />
                    {profile && <Content attendeeProfile={profile} onLogout={() => machine.logout()} />}
                </>
            )
        case "completed":
            router.replace("/classes")
            return <>{profile && <Content attendeeProfile={profile} onLogout={() => machine.logout()} />}</>

    }
}

ProfileMenuPage.Layout = AFFSidebarLayout

export default ProfileMenuPage
